import { useState, useEffect } from 'react'
import { Card, CardContent } from "../../ui/card"
import { Skeleton } from "../../ui/skeleton"
import { Copy, Check } from 'lucide-react'

export function SamplePrompts() {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  
  const prompts = [
    "What is Sales Meeting Framework?",
    "Write a follow-up email",
    "Create PRD for feedback tool",
    "Give article ideas on team culture",
    "Draft checklist for launch",
    "What is PACE program?",
  ]

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setIsLoading(false)
    }, 1600)
    return () => clearTimeout(timer)
  }, [])

  const handleCopy = async (prompt: string, index: number) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(prompt)
      } else {
        const textArea = document.createElement('textarea')
        textArea.value = prompt
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        textArea.style.top = '-999999px'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        try { document.execCommand('copy') } catch {}
        document.body.removeChild(textArea)
      }
      setCopiedIndex(index)
      setTimeout(() => setCopiedIndex(null), 2000)
    } catch {
      setCopiedIndex(index)
      setTimeout(() => setCopiedIndex(null), 2000)
    }
  }

  return (
    <section className="py-20 lg:py-28 relative overflow-hidden bg-gradient-to-br from-blue-50/30 to-indigo-50/30 dark:from-slate-900/50 dark:to-slate-800/50">
      <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-blue-400/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-indigo-400/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="container mx-auto max-w-[1200px] px-10 md:px-[72px] relative z-10">
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
            Try These <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Sample Prompts</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Get started with these example prompts to see what AIRA can do for you.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
          {isLoading ? (
            // Skeleton loading state
            Array.from({ length: 6 }).map((_, index) => (
              <Card 
                key={index}
                className="group relative bg-background/70 backdrop-blur-xl border-border/50 overflow-hidden h-[80px]"
              >
                <CardContent className="p-6 relative z-10 h-full">
                  <div className="flex items-center justify-between gap-4 h-full">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="w-8 h-8 rounded-full" />
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            prompts.map((prompt, index) => (
              <Card 
                key={index}
                className="group relative bg-background/90 backdrop-blur-xl border-border/80 hover:scale-105 transition-all duration-300 shadow-xl -translate-y-1 cursor-pointer overflow-hidden h-[80px]"
                onClick={() => handleCopy(prompt, index)}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-indigo-500/5 to-purple-500/5 opacity-100 transition-opacity duration-300"></div>
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
                <CardContent className="p-6 relative z-10 h-full">
                  <div className="flex items-center justify-between gap-4 h-full">
                    <p className="text-indigo-600 dark:text-indigo-400 group-hover:text-indigo-700 dark:group-hover:text-indigo-300 transition-colors duration-300 leading-relaxed flex-1">
                      "{prompt}"
                    </p>
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-50 dark:bg-indigo-900/30 group-hover:bg-indigo-100 dark:group-hover:bg-indigo-800/40 transition-colors duration-300 flex-shrink-0">
                      {copiedIndex === index ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4 text-indigo-500 group-hover:text-indigo-600 transition-colors duration-300" />
                      )}
                    </div>
                  </div>
                </CardContent>
                <div className="absolute inset-0 ring-1 ring-indigo-500/20 rounded-xl transition-all duration-300"></div>
              </Card>
            ))
          )}
        </div>
        <div className="mt-12 text-center">
          <p className="text-sm text-muted-foreground">Click any prompt to copy it to your clipboard</p>
        </div>
      </div>
    </section>
  )
}


